import {isNil} from 'ramda';

import type {Nullish} from 'shared/web';

import type {
  ChartReportDocument,
  ReportDocumentSummaryItem,
  ReportDocumentValueFormat,
} from './ChartReportDocument';

export function formatReportDocumentValue(
  value: number | Nullish,
  format: ReportDocumentValueFormat,
  document: Pick<ChartReportDocument, 'locale' | 'currency'>,
  options: {compact?: boolean; table?: boolean; percentage?: number | null} = {}
) {
  if (isNil(value)) {
    return '—';
  }
  const fractionDigits = format.maximumFractionDigits ?? 2;
  const maximumFractionDigits = options.compact ? 2 : fractionDigits;
  const numberOptions: Intl.NumberFormatOptions = {
    maximumFractionDigits,
    minimumFractionDigits: options.table ? maximumFractionDigits : 0,
  };
  if (options.compact) {
    numberOptions.notation = 'compact';
  }
  if (format.kind === 'currency' && !options.table) {
    numberOptions.style = 'currency';
    numberOptions.currency = document.currency;
  }
  const suffix = format.kind === 'percentage' ? '%' : format.unit;
  const formatted = new Intl.NumberFormat(document.locale, numberOptions).format(
    value === 0 ? 0 : value
  );
  const primary = suffix && !options.table ? `${formatted} ${suffix}` : formatted;
  if (options.percentage === undefined) {
    return primary;
  }
  // Match the toFixed rounding used by platform/locale on the screen.
  const roundedPercentage = isNil(options.percentage)
    ? null
    : Number(options.percentage.toFixed(2));
  const percentage =
    roundedPercentage === null
      ? '—'
      : `${new Intl.NumberFormat(document.locale, {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(roundedPercentage === 0 ? 0 : roundedPercentage)} %`;
  return `${primary} (${percentage})`;
}

export interface ReportDocumentSecondaryValueOptions {
  document: ChartReportDocument;
  item: ReportDocumentSummaryItem | undefined;
}

export function formatReportDocumentSecondaryValue(props: ReportDocumentSecondaryValueOptions) {
  const secondaryValue = props.item?.secondaryValue;
  if (isNil(secondaryValue)) {
    return undefined;
  }
  return `${formatReportDocumentValue(
    secondaryValue.value,
    {kind: 'number', maximumFractionDigits: 0},
    props.document
  )} ${secondaryValue.label}`;
}
